import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { NIL } from 'uuid';
import { ContractsDto, ContractType } from '../models/contract';

const apiUrl = process.env.REACT_APP_BASIX_API || '';

export interface GenerateContractInput {
  type: ContractType;
  proposalId?: string;
  customerId?: string;
  termMonths?: number; // item 901 — confirmed at generate time (24 or 60)
  fillValues?: Record<string, string>; // item 902 — the attorney's [FILL: label] blanks
}
export interface GenerateContractResult {
  id?: string;
  missing?: string[];
  templateEmpty?: boolean;
  fills?: string[]; // item 902 — unresolved template blanks to prompt for
  fillDefaults?: Record<string, string>; // item 908 — deal-derived suggestions for those blanks
}

// item 719 (S4) — contracts for a project (or a customer, for onboarding S8) + generate.
export const useProjectContracts = (projectId?: string) => useQuery(
  ['contracts', 'project', projectId],
  async (): Promise<ContractsDto> => {
    const { data } = await axios.get(`${apiUrl}Contracts/project/${projectId}`);
    return data;
  },
  { enabled: !!projectId && projectId !== NIL },
);

// item 926 — proposals eligible for contract generation: processed through the guided
// flow to a FINALIZED deposit invoice (job code, pricing, license location all exist).
export interface EligibleProposalRow {
  id: string;
  proposalNumber?: string;
  proposalTitle?: string;
  isRnwy?: boolean;
  processed?: boolean;
}
// item 1178 — includeUnprocessed = LEGACY PAPER mode (admin-tier; server enforces)
export const useEligibleContractProposals = (projectId?: string, includeUnprocessed = false) => useQuery(
  ['contracts', 'eligible', projectId, includeUnprocessed],
  async (): Promise<{ index: EligibleProposalRow[] }> => {
    const { data } = await axios.get(
      `${apiUrl}Contracts/eligible-proposals/${projectId}?includeUnprocessed=${includeUnprocessed}`,
    );
    return data;
  },
  { enabled: !!projectId && projectId !== NIL },
);

export const useCustomerContracts = (customerId?: string) => useQuery(
  ['contracts', 'customer', customerId],
  async (): Promise<ContractsDto> => {
    const { data } = await axios.get(`${apiUrl}Contracts/customer/${customerId}`);
    return data;
  },
  { enabled: !!customerId && customerId !== NIL },
);

// item 1426 — the ARCHIVED lists (project papers / customer-level onboarding). Archive (906)
// only flips a flag and every list filtered it out, so archived contracts had NO view at all.
export const useProjectContractsArchived = (projectId?: string) => useQuery(
  ['contracts', 'project-archived', projectId],
  async (): Promise<ContractsDto> => {
    const { data } = await axios.get(`${apiUrl}Contracts/project/${projectId}/archived`);
    return data;
  },
  { enabled: !!projectId && projectId !== NIL },
);

export const useCustomerContractsArchived = (customerId?: string) => useQuery(
  ['contracts', 'customer-archived', customerId],
  async (): Promise<ContractsDto> => {
    const { data } = await axios.get(`${apiUrl}Contracts/customer/${customerId}/archived`);
    return data;
  },
  { enabled: !!customerId && customerId !== NIL },
);

// item 1426 — restore an archived contract into its list (super-admin only, mirrors archive)
export const useRestoreContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => axios.post(`${apiUrl}Contracts/${id}/restore`, {}),
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

// item 1102 — the NUMBERS panel behind Adjust: every number the deal would fill today, its source,
// and whether the stored document still carries it (null = a typed-only blank, not checkable)
export interface ContractNumberRow {
  label: string;
  suggestion: string | null;
  source: string;
  inDocument: boolean | null;
}
export interface ContractNumbers {
  status: number;
  isDraft: boolean;
  rows: ContractNumberRow[];
  note: string | null;
}
export const getContractNumbers = async (id: string): Promise<ContractNumbers> => {
  const { data } = await axios.get(`${apiUrl}Contracts/${id}/numbers`);
  return data as ContractNumbers;
};

// item 899 (Chad) — the stored contract PDF. A raw <a href> to the API never carried the
// Bearer token (the endpoint is [Authorize]) so row Preview/Download always 401'd; fetch
// as an authenticated blob instead. NEVER revoke the object URL (item 867 — Chrome's
// viewer needs it alive to download from the preview tab).
export const openContractPdf = async (id: string): Promise<void> => {
  const res = await axios.get(`${apiUrl}Contracts/${id}/pdf`, { responseType: 'blob' });
  window.open(URL.createObjectURL(res.data), '_blank');
};

// item 904 — the Word (.docx) rendition for attorney redlines
export const downloadContractDocx = async (id: string, name: string): Promise<void> => {
  const res = await axios.get(`${apiUrl}Contracts/${id}/docx`, { responseType: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(res.data);
  a.download = name.endsWith('.docx') ? name : `${name}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadContractPdf = async (id: string, name: string): Promise<void> => {
  const res = await axios.get(`${apiUrl}Contracts/${id}/pdf`, { responseType: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(res.data);
  a.download = name.endsWith('.pdf') ? name : `${name}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const useGenerateContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: GenerateContractInput): Promise<GenerateContractResult> => {
      const { data } = await axios.post(`${apiUrl}Contracts/generate`, input);
      return data;
    },
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

// item 834 (Chad) — UPLOAD an existing (already-signed) onboarding agreement instead of generating
// one. Sends the file as base64; the server files it as an Executed ContractDocument.
const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result ?? '').split(',')[1] ?? '');
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export const useUploadContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: { customerId: string; projectId?: string; type: number; title?: string; file: File }): Promise<{ id: string }> => {
      const dataBase64 = await fileToBase64(input.file);
      const { data } = await axios.post(`${apiUrl}Contracts/upload`, {
        customerId: input.customerId,
        projectId: input.projectId ?? null, // item 977 — file under a PROJECT
        type: input.type,
        title: input.title || input.file.name,
        name: input.file.name,
        contentType: input.file.type,
        dataBase64,
      });
      return data;
    },
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

// preview WITHOUT saving — POST returns a PDF blob we open in a new tab.
// item 896 — a failure used to be swallowed into a generic "save a template first" toast even
// when the template existed; decode the error blob so the SERVER's reason surfaces.
export const previewContract = async (input: GenerateContractInput): Promise<void> => {
  try {
    const res = await axios.post(`${apiUrl}Contracts/preview`, input, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    window.open(url, '_blank');
  } catch (e: any) {
    const blob = e?.response?.data;
    if (blob instanceof Blob) throw new Error((await blob.text()) || 'Preview failed.');
    throw e;
  }
};

// item 721 (S6) — send for signature via SignWell (customer → admin). Returns the SignWell edit
// URL where the admin places fields + sends. Opens it in a new tab.
export const useSendContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: string): Promise<{ documentId?: string; editUrl?: string }> => {
      const { data } = await axios.post(`${apiUrl}Contracts/${id}/send`, {});
      return data;
    },
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

// item 910 — the review lifecycle: fetch/adjust the body (Draft only), finalize, reopen
// item 1428 — the body comes with the header's Effective Date (editable on a draft)
export const getContractBody = async (id: string): Promise<{ body: string; effectiveDate: string }> => {
  const { data } = await axios.get(`${apiUrl}Contracts/${id}/body`);
  return { body: data.body as string, effectiveDate: data.effectiveDate as string };
};

export const saveContractBody = async (
  id: string,
  body: string,
  effectiveDate?: string, // item 1428 — "YYYY-MM-DD"; omit to leave the header date alone
  replaceDateInBody = false,
): Promise<void> => {
  await axios.post(`${apiUrl}Contracts/${id}/body`, { body, effectiveDate: effectiveDate || undefined, replaceDateInBody });
};

export const useFinalizeContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => axios.post(`${apiUrl}Contracts/${id}/finalize`, {}),
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

export const useReopenContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => axios.post(`${apiUrl}Contracts/${id}/reopen`, {}),
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

// item 906 — archive a stale contract (super-admin only; the lists filter archived out)
export const useArchiveContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => axios.post(`${apiUrl}Contracts/${id}/archive`, {}),
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

// item 1122 — a send abandoned in SignWell's editor: the envelope is still a SignWell DRAFT
// (no emails went out), so the admin can either RESUME the editor with a fresh link or CANCEL
// the send (deletes the SignWell draft, contract falls back to Finalized).
export const useContractSignWellEditLink = () => useMutation(
  async (id: string): Promise<{ editUrl?: string }> => {
    const { data } = await axios.post(`${apiUrl}Contracts/${id}/signwell-edit-link`, {});
    return data;
  },
);

export const useUnsendContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => axios.post(`${apiUrl}Contracts/${id}/unsend`, {}),
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

export const useRefreshContractStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    // item 1183-4c — a contract with no SignWell envelope (uploaded executed paper, legacy
    // rows) answers 404: that's a fact about the row, not a failure. Swallow it so the
    // mount-time auto-polls don't spray errors; anything else still rejects.
    (id: string) => axios.post(`${apiUrl}Contracts/${id}/refresh-status`, {}).catch((e) => {
      if (e?.response?.status === 404) return null;
      throw e;
    }),
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};

// item 1105 (Chad) — REGENERATE a draft: re-run the merge against the CURRENT template + deal
// so template/pricing fixes reach a contract that was generated before them. Manual Adjust
// edits are replaced (that's the point). Draft-only, admin-only, enforced server-side.
export const useRegenerateContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    // item 1409 — optional answers for the blanks the deal can't fill (the fill-in dialog)
    (args: { id: string; fillValues?: Record<string, string> }) => axios.post(`${apiUrl}Contracts/${args.id}/regenerate`, { fillValues: args.fillValues ?? {} }),
    { onSuccess: () => queryClient.invalidateQueries('contracts') },
  );
};
